import { fileHubApi } from '@/features/file-hub/api';

/** Trigger a browser file download from a Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const PLACEHOLDER_URLS = new Set(['', '#', 'javascript:void(0)', 'javascript:;']);

export function isPlaceholderUrl(url: string | null | undefined): boolean {
  if (!url) return true;
  const trimmed = url.trim();
  if (PLACEHOLDER_URLS.has(trimmed)) return true;
  return trimmed.startsWith('#');
}

export function isHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

/** HTTP(S) or site-relative path suitable for browser download (not a placeholder). */
export function isDownloadableUrl(url: string | null | undefined): boolean {
  if (isPlaceholderUrl(url)) return false;
  const trimmed = url!.trim();
  return isHttpUrl(trimmed) || trimmed.startsWith('/');
}

export function downloadFromUrl(url: string, filename?: string): void {
  const link = document.createElement('a');
  link.href = url;
  if (filename) link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function openInNewTab(url: string | null | undefined): boolean {
  if (!isDownloadableUrl(url)) return false;
  window.open(url!.trim(), '_blank', 'noopener,noreferrer');
  return true;
}

export type DownloadSource =
  | { kind: 'file_hub'; fileId: string }
  | { kind: 'url'; url: string; filename?: string };

export type DownloadableFields = {
  file_id?: string | null;
  fileId?: string | null;
  download_url?: string | null;
  file_url?: string | null;
  file_path?: string | null;
  storage_key?: string | null;
};

export function resolveDownloadSource(obj: DownloadableFields): DownloadSource | null {
  const fileId = obj.file_id ?? obj.fileId;
  if (fileId) return { kind: 'file_hub', fileId };

  const url = obj.download_url ?? obj.file_url ?? obj.file_path ?? obj.storage_key;
  if (isDownloadableUrl(url)) {
    return { kind: 'url', url: url!.trim() };
  }
  return null;
}

export function canDownload(obj: DownloadableFields): boolean {
  return resolveDownloadSource(obj) !== null;
}

export const FILE_NOT_LINKED_TITLE = 'файл не привязан';

export async function executeDownload(
  obj: DownloadableFields,
  filename?: string,
): Promise<void> {
  const source = resolveDownloadSource(obj);
  if (!source) return;

  if (source.kind === 'url') {
    downloadFromUrl(source.url, filename ?? source.filename);
    return;
  }

  const res = await fileHubApi.downloadFile(source.fileId);
  const blob = await fileHubApi.downloadFileStream(source.fileId, res.version_id);
  downloadBlob(blob, res.original_name || filename || 'download');
}
